import type { Frequency } from 'models/series';
import { FINANCIAL_NAME_TO_COIN } from 'utils/cryptoChartData';
import { getPublicImage } from 'utils/images';

export { is15MinuteKnockoutTicker } from 'utils/cryptoChartData';

// Index series (NASDAQ / S&P) only have a meaningful numeric forecast at
// longer cadences (weekly / monthly / annual). Their short-cadence variants
// (e.g. KXINXU, KXNASDAQ100U daily, or hourly/15-min) don't accumulate enough
// forecast history to render anything, so the forecast chart is suppressed for
// them. Keyed off series `frequency` (not ticker shape) so monthly/yearly
// ranges like KXINXMAXMM and KXNASDAQ100Y keep their forecast.
const SHORT_FORECAST_FREQUENCIES = new Set<Frequency>(['daily', 'hourly', 'fifteen_min']);

// These weather series intentionally never show the numeric forecast. Keep the decision
// outside EventNumericHistoryGraph so callers can avoid loading that chart and starting
// its forecast-history queries in the first place.
const FORCE_HIDDEN_NUMERIC_FORECAST_SERIES = new Set([
    'KXHIGHNY',
    'KXHIGHMIA',
    'KXHIGHCHI',
    'KXHIGHAUS',
    'KXHIGHDEN',
    'KXHIGHPHIL',
    'KXHIGHHOU',
    'KXHIGHLAX',
]);

export type NumericForecastSuppressionReason = 'force_hidden_series' | 'short_cadence_index';

// Minimum event volume for a numeric forecast to be worth rendering. Below
// this, a freshly-opened market (e.g. the current month's KXINXMAXMM on its
// first day) has too little history to plot, so the forecast chart — and the
// toggle that leads to it — is suppressed. Shared with the `poorGraph` guard
// in EventNumericHistoryGraph so the toggle decision and the chart body agree.
export const MIN_FORECAST_VOLUME = 200;

export const isIndexForecastSuppressed = (seriesTicker: string, frequency: Frequency | undefined): boolean => {
    const isIndexSeries = seriesTicker.includes('INX') || seriesTicker.includes('NASDAQ');
    return isIndexSeries && !!frequency && SHORT_FORECAST_FREQUENCIES.has(frequency);
};

export const getNumericForecastSuppressionReason = (
    seriesTicker: string,
    frequency: Frequency | undefined,
): NumericForecastSuppressionReason | undefined => {
    if (FORCE_HIDDEN_NUMERIC_FORECAST_SERIES.has(seriesTicker)) return 'force_hidden_series';
    if (isIndexForecastSuppressed(seriesTicker, frequency)) return 'short_cadence_index';
    return undefined;
};

type CommodityFinancialName =
    | 'BRENT'
    | 'COCOA'
    | 'COFFEE'
    | 'COPPER'
    | 'CORN'
    | 'DJI'
    | 'GOLD'
    | 'HOIL'
    | 'LCATTLE'
    | 'LITHIUM'
    | 'NATGAS'
    | 'NICKEL'
    | 'OIL'
    | 'PALLADIUM'
    | 'PLATINUM'
    | 'SILVER'
    | 'SOYBEANS'
    | 'SUGAR'
    | 'WHEAT'
    | 'WTI'
    // Ornn GPU compute prices, $/GPU-hour.
    | 'H100_SXM'
    | 'H200'
    | 'A100_SXM4'
    | 'B200'
    | 'RTX_5090'
    // Forex pairs — served by the commodities live-data lane (Pyth FX spot
    // feeds); keep in sync with svc-live-data/adapters/commodities/ticker.go.
    | 'EURUSD'
    | 'USDJPY'
    | 'GBPUSD'
    | 'AUDUSD'
    | 'NZDUSD'
    | 'USDCAD'
    | 'USDCHF'
    | 'USDMXN'
    | 'USDBRL'
    | 'USDINR'
    | 'USDNOK'
    | 'USDSEK';

const COMMODITY_STALE_GAP_THRESHOLD_MS = 2 * 60 * 1000;

interface EventFinancialDetectionInput {
    seriesTicker?: string;
}

type CompanyKpiDetectionInput = {
    series?: {
        productMetadata?: {
            subcategories?: Record<string, string[]>;
        };
    } | null;
} | null;

const COMMODITY_DEFINITIONS: Array<{
    aliases: string[];
    financialName: CommodityFinancialName;
    seriesPattern: RegExp;
}> = [
    { aliases: ['BRENT'], financialName: 'BRENT', seriesPattern: /^KXBRENT(?:D|W|MON)?$/ },
    { aliases: ['COCOA'], financialName: 'COCOA', seriesPattern: /^KXCOCOA(?:D|W|MON)?$/ },
    { aliases: ['COFFEE'], financialName: 'COFFEE', seriesPattern: /^KXCOFFEE(?:D|W|MON)?$/ },
    { aliases: ['COPPER'], financialName: 'COPPER', seriesPattern: /^KXCOPPER(?:D|W|MON)?$/ },
    { aliases: ['CORN'], financialName: 'CORN', seriesPattern: /^KXCORN(?:D|W|MON)?$/ },
    // Dow Jones hourly ladders — served by the commodities live-data lane
    // (Pyth 24/7 US30 index), keep in sync with
    // svc-live-data/adapters/commodities/ticker.go.
    // KXDJI = hourly ladders; KXDJIA = monthly/annual "how high" ranges.
    { aliases: ['DJI', 'DOWJONES'], financialName: 'DJI', seriesPattern: /^KXDJIA?$/ },
    { aliases: ['GOLD'], financialName: 'GOLD', seriesPattern: /^KXGOLD(?:D|H|W|MON|PRICE|15M)?$/ },
    { aliases: ['HOIL', 'HEATINGOIL'], financialName: 'HOIL', seriesPattern: /^KXHOIL(?:D|W|MON)?$/ },
    {
        aliases: ['LCATTLE', 'LIVECATTLE'],
        financialName: 'LCATTLE',
        seriesPattern: /^KXLCATTLE(?:D|W|MON)?$/,
    },
    { aliases: ['LITHIUM'], financialName: 'LITHIUM', seriesPattern: /^KXLITHIUM(?:W|MON)?$/ },
    {
        aliases: ['NATGAS', 'NATURALGAS'],
        financialName: 'NATGAS',
        seriesPattern: /^KXNATGAS(?:D|W|MON)?$/,
    },
    { aliases: ['NICKEL'], financialName: 'NICKEL', seriesPattern: /^KXNICKEL(?:D|W|MON)?$/ },
    { aliases: ['OIL'], financialName: 'OIL', seriesPattern: /^KXOIL(?:W)?$/ },
    { aliases: ['PALLADIUM'], financialName: 'PALLADIUM', seriesPattern: /^KXPALLADIUM(?:D|H|W|MON)?$/ },
    { aliases: ['PLATINUM'], financialName: 'PLATINUM', seriesPattern: /^KXPLATINUM(?:D|H|W|MON)?$/ },
    { aliases: ['SILVER'], financialName: 'SILVER', seriesPattern: /^KXSILVER(?:D|H|W|MON|15M)?$/ },
    {
        aliases: ['SOYBEAN', 'SOYBEANS'],
        financialName: 'SOYBEANS',
        seriesPattern: /^KXSOYBEANS?(?:D|W|MON)?$/,
    },
    { aliases: ['SUGAR'], financialName: 'SUGAR', seriesPattern: /^KXSUGAR(?:D|W|MON)?$/ },
    { aliases: ['WHEAT'], financialName: 'WHEAT', seriesPattern: /^KXWHEAT(?:D|W|MON)?$/ },
    { aliases: ['WTI', 'CRUDEOIL'], financialName: 'WTI', seriesPattern: /^KXWTI(?:E|EU|H|W|MAXM?|MINM?|15M)?$/ },
    // Ornn GPU series — keep in sync with svc-live-data/adapters/commodities/ornn/ticker.go.
    { aliases: ['H100', 'H100SXM'], financialName: 'H100_SXM', seriesPattern: /^KXH100(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['H200'], financialName: 'H200', seriesPattern: /^KXH200(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['A100', 'A100SXM4'], financialName: 'A100_SXM4', seriesPattern: /^KXA100(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['B200'], financialName: 'B200', seriesPattern: /^KXB200(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['RTX5090', 'RTX_5090'], financialName: 'RTX_5090', seriesPattern: /^KXRTX5090(?:W|MON|Q|MAX|MIN)?$/ },

    // Forex pairs, served by the commodities live-data lane (Pyth FX spot
    // feeds — keep in sync with svc-live-data/adapters/commodities/ticker.go).
    // Each pattern covers the pair's listed series families: the active *AW
    // weeklies and daily ranges, plus dormant hourlies/up-downs, yearly
    // highs/lows, and settled one-offs, so historical pages chart too.
    // NOTE: this per-pair whitelist (and this whole per-series mapping) is
    // fine at a dozen pairs — when it becomes too unmanageable, do away with
    // it and drive detection off the events' product_metadata
    // live_data_event_type instead, which the backend can stamp at creation.
    {
        aliases: ['EURUSD'],
        financialName: 'EURUSD',
        seriesPattern: /^KXEURUSD(?:AH|AW|D|E|EOY|H|HA|MAX|MIN|PARITY|Q|W)?$/,
    },
    { aliases: ['USDJPY'], financialName: 'USDJPY', seriesPattern: /^KXUSDJPY(?:AH|AW|D|E|EU|H|MAX|MIN|Q|W)?$/ },
    { aliases: ['GBPUSD'], financialName: 'GBPUSD', seriesPattern: /^KXGBPUSD(?:AW|D|E|H)?$/ },
    { aliases: ['AUDUSD'], financialName: 'AUDUSD', seriesPattern: /^KXAUDUSD(?:AW|H|Q|W)?$/ },
    { aliases: ['NZDUSD'], financialName: 'NZDUSD', seriesPattern: /^KXNZDUSD(?:AW)?$/ },
    { aliases: ['USDCAD'], financialName: 'USDCAD', seriesPattern: /^KXUSDCAD(?:AW|W)?$/ },
    { aliases: ['USDCHF'], financialName: 'USDCHF', seriesPattern: /^KXUSDCHF(?:AW)?$/ },
    { aliases: ['USDMXN'], financialName: 'USDMXN', seriesPattern: /^KXUSDMXN(?:AW)?$/ },
    { aliases: ['USDBRL'], financialName: 'USDBRL', seriesPattern: /^KXUSDBRL(?:AW|MAX|MAXM)?$/ },
    { aliases: ['USDINR'], financialName: 'USDINR', seriesPattern: /^KXUSDINR(?:AW)?$/ },
    { aliases: ['USDNOK'], financialName: 'USDNOK', seriesPattern: /^KXUSDNOK(?:AW)?$/ },
    { aliases: ['USDSEK'], financialName: 'USDSEK', seriesPattern: /^KXUSDSEK(?:AW)?$/ },
];

const COMMODITY_FINANCIAL_NAMES = new Set<CommodityFinancialName>(
    COMMODITY_DEFINITIONS.map((definition) => definition.financialName),
);

const commoditySeriesTickerToFinancialName = (seriesTicker: string): CommodityFinancialName | undefined => {
    return COMMODITY_DEFINITIONS.find((definition) => definition.seriesPattern.test(seriesTicker))?.financialName;
};

export const seriesTickerToFinancialName = (seriesTicker: string): string | undefined => {
    const normalizedTicker = seriesTicker.toUpperCase();
    if (normalizedTicker.includes('NASDAQ')) return 'QQQ';
    if (normalizedTicker.includes('INX') && normalizedTicker !== 'KXJOINXAI') return 'SPX';
    if (normalizedTicker.includes('EURUSD')) return 'EURUSD';
    if (normalizedTicker.includes('USDJPY')) return 'USDJPY';
    if (normalizedTicker.includes('USDRUB')) return 'USDRUB';
    if (['BTCD', 'BTC', 'KXBTCD', 'KXBTC', 'KXBTC15M'].includes(normalizedTicker)) return 'BTC';
    if (['ETHD', 'ETH', 'KXETHD', 'KXETH', 'KXETH15M'].includes(normalizedTicker)) return 'ETH';
    if (['KXDOGED', 'KXDOGE', 'KXDOGEMAXW', 'KXDOGEMAXY', 'KXDOGE15M'].includes(normalizedTicker)) return 'DOGE';
    if (['KXBNBD', 'KXBNB', 'KXBNB15M'].includes(normalizedTicker)) return 'BNB';
    if (['KXHYPED', 'KXHYPE', 'KXHYPE15M'].includes(normalizedTicker)) return 'HYPE';
    if (['KXZECD', 'KXZEC', 'KXZEC15M'].includes(normalizedTicker)) return 'ZEC';
    if (['KXNEARD', 'KXNEAR', 'KXNEAR15M'].includes(normalizedTicker)) return 'NEAR';
    if (['KXSHIBAD', 'KXSHIBA', 'KXSHIBAMAXW', 'KXSHIBAMAXY'].includes(normalizedTicker)) return 'SHIBA';
    if (['SOLD', 'SOL', 'KXSOLD', 'KXSOL', 'KXSOL15M'].includes(normalizedTicker)) return 'SOL';
    if (['KXXRPD', 'KXXRP', 'KXXRP15M'].includes(normalizedTicker)) return 'XRP';

    return commoditySeriesTickerToFinancialName(normalizedTicker);
};

export const isFinancialNameCrypto = (financialName?: string): boolean =>
    !!financialName && financialName in FINANCIAL_NAME_TO_COIN;

export const isFinancialNameCommodity = (financialName?: string): boolean =>
    !!financialName && COMMODITY_FINANCIAL_NAMES.has(financialName as CommodityFinancialName);

// Commodities whose feed is not named after our financial name. WTI settles against the PYTHOIL index
// (`Commodities.Index.PYTHOIL/USD`), so `PYTH:WTI` is rejected and only `PYTH:PYTHOIL` returns a series.
const LIVE_DATA_ASSET_OVERRIDES: Record<string, string> = {
    WTI: 'PYTHOIL',
};

/**
 * The identifier `/v1/live_data/assets/<asset>` expects for a financial name.
 *
 * Crypto is addressed by the bare name. The Pyth-backed commodities are namespaced (`PYTH:GOLD`), and the
 * bare form is rejected, which is why a commodity slot read as having no asset feed at all.
 */
export const financialNameToLiveDataAsset = (financialName: string | undefined): string | undefined => {
    if (!financialName) return undefined;
    if (!isFinancialNameCommodity(financialName)) return financialName;
    return `PYTH:${LIVE_DATA_ASSET_OVERRIDES[financialName] ?? financialName}`;
};

/**
 * True when a series tracks a single financial underlying with a live external
 * price feed (indices, FX, rates, WTI, single-asset crypto) — i.e. the event's
 * chart should show the underlying price, not a probability forecast.
 *
 * Crypto is matched against the exact-match allowlist inside
 * `seriesTickerToFinancialName`, never a bare `BTC`/`SOL`/`DOGE` substring, so
 * binary threshold or comparison series (`KXBTC2026250`, `KXSOL26500`,
 * `KXDOGEMAX1`, `KXBTCVSETH`, …) are correctly excluded and render the binary
 * chart instead of a broken underlying view.
 */
export const isFinancialUnderlyingSeries = (seriesTicker: string): boolean => {
    const ticker = seriesTicker.toUpperCase();
    if (ticker.includes('NASDAQ')) return true;
    if (ticker.includes('INX') && ticker !== 'KXJOINXAI') return true;
    if (ticker.includes('EURUSD')) return true;
    if (ticker.includes('USDJPY')) return true;
    if (ticker.includes('USDRUB')) return true;
    if (ticker.includes('TNOTE')) return true;
    if (ticker.includes('WTI')) return true;
    return isFinancialNameCrypto(seriesTickerToFinancialName(ticker));
};

export const eventToFinancialName = (event?: EventFinancialDetectionInput | null): string | undefined => {
    return seriesTickerToFinancialName(event?.seriesTicker ?? '');
};

export const isCompanyKpiEvent = (event?: CompanyKpiDetectionInput): boolean => {
    const subcategoriesByCategory = event?.series?.productMetadata?.subcategories;
    if (!subcategoriesByCategory) {
        return false;
    }

    return Object.values(subcategoriesByCategory).some((subcategories) =>
        subcategories.some((subcategory) => subcategory.trim().toLowerCase() === 'kpis'),
    );
};

export const seriesTickerToIndicesName = (seriesTicker: string): string | undefined => {
    if (['PRESINDEXD', 'CONTROL'].includes(seriesTicker)) return 'ELECTIONS_PRES_INDICES';

    return undefined;
};

export const getBackupImagePath = (isDarkMode: boolean): string => {
    return `${getPublicImage(isDarkMode ? 'empty_graph_dark.png' : 'empty_graph_light.png')}`;
};

export const getCommodityDataAgeMs = (lastUpdatedAtMs: number | undefined, nowMs: number): number | undefined => {
    if (!lastUpdatedAtMs) return undefined;

    return Math.max(0, nowMs - lastUpdatedAtMs);
};

export const formatLastUpdatedDataAge = (ageMs: number): string => {
    const totalMinutes = Math.floor(ageMs / 60_000);

    if (totalMinutes < 1) {
        return '<1m ago';
    }

    if (totalMinutes < 60) {
        return `${totalMinutes}m ago`;
    }

    const totalHours = Math.floor(totalMinutes / 60);
    if (totalHours >= 24) {
        const totalDays = Math.floor(totalHours / 24);
        const remainingHours = totalHours % 24;

        if (remainingHours === 0) {
            return `${totalDays}d ago`;
        }

        return `${totalDays}d ${remainingHours}h ago`;
    }

    const remainingMinutes = totalMinutes % 60;

    if (remainingMinutes === 0) {
        return `${totalHours}h ago`;
    }

    return `${totalHours}h ${remainingMinutes}m ago`;
};

export const shouldExtendCommodityDomainToNow = (ageMs: number): boolean => {
    return ageMs >= COMMODITY_STALE_GAP_THRESHOLD_MS;
};

const CENTS_COMMODITIES: ReadonlySet<CommodityFinancialName> = new Set([
    'CORN',
    'WHEAT',
    'SOYBEANS',
    'SUGAR',
    'COFFEE',
    'LCATTLE',
]);

export const isCentsCommodity = (financialName?: string): boolean =>
    !!financialName && CENTS_COMMODITIES.has(financialName as CommodityFinancialName);

const CNY_COMMODITIES: ReadonlySet<CommodityFinancialName> = new Set(['LITHIUM']);

export const isCnyCommodity = (financialName?: string): boolean =>
    !!financialName && CNY_COMMODITIES.has(financialName as CommodityFinancialName);

// Surface price as `$X.XX/hr` instead of bare dollars.
const PER_HOUR_COMMODITIES: ReadonlySet<CommodityFinancialName> = new Set([
    'H100_SXM',
    'H200',
    'A100_SXM4',
    'B200',
    'RTX_5090',
]);

export const isPerHourCommodity = (financialName?: string): boolean =>
    !!financialName && PER_HOUR_COMMODITIES.has(financialName as CommodityFinancialName);

// Forex rates render as bare rates ("1.1532"), never currency-prefixed.
const FOREX_COMMODITIES: ReadonlySet<CommodityFinancialName> = new Set([
    'EURUSD',
    'USDJPY',
    'GBPUSD',
    'AUDUSD',
    'NZDUSD',
    'USDCAD',
    'USDCHF',
    'USDMXN',
    'USDBRL',
    'USDINR',
    'USDNOK',
    'USDSEK',
]);

export const isFinancialNameForex = (financialName?: string): boolean =>
    !!financialName && FOREX_COMMODITIES.has(financialName as CommodityFinancialName);

export const getCommodityUnitSymbol = (financialName?: string): string => {
    if (isFinancialNameForex(financialName)) return '';
    if (isCentsCommodity(financialName)) return '¢';
    if (isCnyCommodity(financialName)) return '¥';
    return '$';
};

export const getCommodityDomainEndDate = (
    ageMs: number | undefined,
    nowMs: number,
    isOpen?: boolean,
    closeDate?: string,
): Date | undefined => {
    if (ageMs === undefined || !shouldExtendCommodityDomainToNow(ageMs)) {
        return undefined;
    }

    if (isOpen === false && closeDate) {
        const parsedCloseDateMs = new Date(closeDate).getTime();
        if (Number.isFinite(parsedCloseDateMs)) {
            return new Date(parsedCloseDateMs);
        }
    }

    return new Date(nowMs);
};
